import { bottomNavigationBarVisibleAtom, getCurrentTutorial, isLoggedInAtom, isStartingAtom } from 'app/atoms';
import { EmptyGraphic } from 'app/components/empty-graphic';
import { withErrorBoundary } from 'app/components/error-boundary';
import { PostsList } from 'app/components/posts-list';
import { useTutorialOverlay, useTutorialRef, useTutorials } from 'app/components/tutorial-overlay';
import UnavailabilityBanner from 'app/components/unavailability-banner';
import { images } from 'app/design/images';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';

import { IconEdit, IconMessages, IconSend } from '@tabler/icons-react-native';
import { ShowErrors } from 'app/components/show-errors';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useFocusEffect } from '@react-navigation/native';
import { isBlockingModalVisible as isBlockingModalVisibleNow } from 'app/hooks/use-is-blocking-modal-visible';
import { Event, track } from 'app/lib/analytics';
import { isQuickOffer } from 'app/network/listing';
import { usePostsByListing } from 'app/network/post';
import { useSocialFeedFeature } from 'app/hooks/use-social-feed-feature';
import { Placeholder, PlaceholderLine, PlaceholderMedia, ShineOverlay } from 'app/overrides/rn-placeholder';
import { ListingContext } from 'app/screens/shared/one-listing/components/ListingProvider';
import {
  OthersListing,
  OthersListingAnimatedChrome,
  OthersListingHeader,
} from 'app/screens/shared/one-listing/components/others-listing';
import { PosterSection } from 'app/screens/shared/one-listing/components/poster-section';
import { useListing } from 'app/screens/shared/one-listing/components/useListing';
import { navigateToAllChannels } from 'app/screens/chat/navigator-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import { ListingType } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { Tutorial, TutorialRef } from 'app/types/tutorial';
import { WhiteLabel } from 'app/types/white-labels';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useIsGuestWeb, usePageTitle, useUISize } from 'app/utils/web-helpers';
import type { TFunction } from 'i18next';
import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openURL } from 'expo-linking';
import {
  findNodeHandle,
  Platform,
  RefreshControl,
  View,
  type ScrollView,
  type TextInput,
  type ViewProps,
  useWindowDimensions,
} from 'react-native';
import type { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Tabs } from 'app/components/tabs';
import { useAtomValue, useSetAtom } from 'jotai';
import { FeatureFlag, getShareListingUrl, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { shouldRedirectToSharedListing } from './should-redirect-to-shared-listing';
import { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { ConciergerieSection } from 'app/screens/shared/one-listing/components/conciergerie-section';
import { AssistantBottomSheet, AssistantBottomSheet2 } from 'app/screens/new';
import { navigationRef } from 'app/lib/root-navigation';
import {
  ListingAvailabilityCalendarProvider,
  useListingDateProposalAction,
} from 'app/screens/shared/one-listing/components/listing-availability-calendar';
import { shouldShowListingAvailabilityCalendar } from './components/listing-availability';
const theme = createDSTheme();

type OneListingPageRef = ScrollView | KeyboardAwareScrollView;

const COMMENT_INPUT_EXTRA_SCROLL_HEIGHT =
  theme.spacing['spacing.general.x12'] * 4 + theme.spacing['spacing.general.x4'];

function scheduleContactTutorial(
  timerRef: { current: ReturnType<typeof setTimeout> | undefined },
  mountedRef: { current: boolean },
  startTutorial: ReturnType<typeof useTutorials>['startTutorial'],
  settleDelay: number,
) {
  if (timerRef.current) clearTimeout(timerRef.current);
  timerRef.current = setTimeout(() => {
    if (!mountedRef.current) return;
    if (isBlockingModalVisibleNow({ allowListingModal: true })) {
      scheduleContactTutorial(timerRef, mountedRef, startTutorial, 180);
      return;
    }
    if (getCurrentTutorial()) return;
    startTutorial(Tutorial.ContactMember, undefined, { allowListingModal: true });
  }, settleDelay);
}

function ListingActionAccessory({
  contactButtonRef,
  contactError,
  contactPending,
  isSmallUI,
  onContactButtonLayout,
  onContactPress,
  paddingBottom,
  paddingHorizontal,
}: {
  contactButtonRef: React.ComponentProps<typeof Accessory>['innerRef'];
  contactError: Error | null;
  contactPending: boolean;
  isSmallUI: boolean;
  onContactButtonLayout: () => void;
  onContactPress: () => void;
  paddingBottom: number;
  paddingHorizontal: number;
}) {
  const { t } = useTranslation('one-listing');
  const dateProposalAction = useListingDateProposalAction();
  const isWeb = Platform.OS === 'web';
  const shadowStyle = {
    shadowColor: 'rgba(0, 0, 0, 1)',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.16,
    shadowRadius: 12,
    elevation: 4,
  };

  return (
    <Accessory
      innerRef={contactButtonRef}
      onLayout={onContactButtonLayout}
      collapsable={false}
      elevated
      style={{
        paddingHorizontal,
        paddingTop: theme.spacing['spacing.general.x2'],
        paddingBottom: isSmallUI ? paddingBottom : 0,
      }}
    >
      <View style={!isWeb ? shadowStyle : {}}>
        <Button
          testID={dateProposalAction ? 'listing-date-proposal-button' : 'listing-contact-member-button'}
          authorize
          authorizationSource={dateProposalAction ? 'propose-dates' : 'contact'}
          flex={1}
          justifyContent="center"
          loading={dateProposalAction?.isSubmitting ?? contactPending}
          minHeight={52}
          onPress={dateProposalAction?.onPress ?? onContactPress}
          style={[
            {
              backgroundColor: theme.colors['surface.inverse.default'],
              borderRadius: theme.borderRadii['radius.xl'],
            },
            isWeb ? shadowStyle : {},
          ]}
          variant="primary"
        >
          {dateProposalAction ? (
            <IconSend color="white" size={20} style={{ marginRight: theme.spacing['spacing.general.x2'] }} />
          ) : (
            <IconMessages color="white" size={20} style={{ marginRight: theme.spacing['spacing.general.x2'] }} />
          )}

          <Text color="text.inverse.primary" fontWeight="700" textAlign="center" variant="bodyBase">
            {dateProposalAction ? t('availabilityCalendar.proposeDates') : t('headerSection.contactButtonText')}
          </Text>
        </Button>
      </View>
      <ShowErrors error={contactError} />
    </Accessory>
  );
}

function UnavailableListingNotice({ t }: { t: TFunction }) {
  return (
    <Column flex={1} justifyContent="center" padding="spacing.general.x4">
      <EmptyGraphic source={images.trash} />
      <Text
        fontSize={16}
        fontWeight="600"
        marginBottom="spacing.general.x4"
        marginTop="spacing.general.x12"
        textAlign="center"
      >
        {t('unavailableTitle', { ns: 'one-listing' })}
      </Text>
      <Text color="text.secondary" fontSize={16} fontWeight="400" textAlign="center">
        {t('unavailableMessage', { ns: 'one-listing' })}
      </Text>
    </Column>
  );
}

function LoadedOneListingScreen({
  defaultPaddingStyle,
  fromNewScreen,
  handleCommentInputBlur,
  handleCommentInputFocus,
  isCommentInputFocused,
  isSmallUI,
  isUnavailabilityBannerVisible,
  listingHook,
  pageRef,
  scrollOffsetRef,
  scrollViewRef,
  setIsUnavailabilityBannerVisible,
  setUnavailabilityBannerHeight,
  t,
  unavailabilityBannerHeight,
}: {
  defaultPaddingStyle: ReturnType<typeof usePagePadding>;
  fromNewScreen?: boolean;
  handleCommentInputBlur: () => void;
  handleCommentInputFocus: (input: TextInput | null) => void;
  isCommentInputFocused: boolean;
  isSmallUI: boolean;
  isUnavailabilityBannerVisible: boolean;
  listingHook: ReturnType<typeof useListing>;
  pageRef: React.RefObject<OneListingPageRef | null>;
  scrollOffsetRef: React.RefObject<number>;
  scrollViewRef: React.RefObject<ScrollView | null>;
  setIsUnavailabilityBannerVisible: (visible: boolean) => void;
  setUnavailabilityBannerHeight: (height: number) => void;
  t: TFunction;
  unavailabilityBannerHeight: number;
}) {
  const { isAuthor, listingQuery, userQuery } = listingHook;
  const listing = listingQuery.data!;
  const isUnavailable = listing.isCurrentlyUnavailable ?? false;
  const showUnavailabilityBanner =
    isUnavailable && listing.userId !== userQuery.data?.id && isUnavailabilityBannerVisible;

  return (
    <ListingContext.Provider value={listingHook}>
      {showUnavailabilityBanner ? (
        <UnavailabilityBanner
          withInsets
          onDismiss={() => setIsUnavailabilityBannerVisible(false)}
          onHeightChange={setUnavailabilityBannerHeight}
        />
      ) : null}
      <ListingWithTabs
        defaultPaddingStyle={defaultPaddingStyle}
        handleCommentInputBlur={handleCommentInputBlur}
        handleCommentInputFocus={handleCommentInputFocus}
        listingHook={listingHook}
        pageRef={pageRef}
        t={t}
        scrollViewRef={scrollViewRef}
        scrollOffsetRef={scrollOffsetRef}
        variant={{
          origin: fromNewScreen ? 'new-listing' : 'standard',
          viewer: isAuthor ? 'author' : 'visitor',
          commentInput: isCommentInputFocused ? 'focused' : 'blurred',
          viewport: isSmallUI ? 'small' : 'large',
          unavailabilityBanner: showUnavailabilityBanner
            ? { state: 'visible', height: unavailabilityBannerHeight }
            : { state: 'hidden' },
        }}
      />
    </ListingContext.Provider>
  );
}

const OneListingScreenComponent: NavigationScreen<'one-listing'> = ({ navigation, route }) => {
  const listingHook = useListing({ route });
  const pageRef = React.useRef<OneListingPageRef | null>(null);
  const parentState = navigation.getParent()?.getState();
  const isInModal = parentState?.routes[parentState.index]?.name === 'listingModal';
  const defaultPaddingStyle = usePagePadding(isInModal);
  const [isCommentInputFocused, setIsCommentInputFocused] = useState(false);
  const [isUnavailabilityBannerVisible, setIsUnavailabilityBannerVisible] = useState(true);
  const [unavailabilityBannerHeight, setUnavailabilityBannerHeight] = useState(0);
  const isSmallUI = useUISize().isSmallUI;
  const setBottomNavigationBarVisible = useSetAtom(bottomNavigationBarVisibleAtom);
  const scrollViewRef = useRef<ScrollView | null>(null);
  const scrollOffsetRef = useRef(0);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isStarting = useAtomValue(isStartingAtom);
  const isGuestWeb = useIsGuestWeb();

  useFocusEffect(() => {
    if (Platform.OS === 'web') {
      setBottomNavigationBarVisible(true);
      return;
    }

    setBottomNavigationBarVisible(false);
    return () => setBottomNavigationBarVisible(true);
  });

  useEffect(() => {
    if (!shouldRedirectToSharedListing({ isLoggedIn, isSmallUI, isStarting, isWeb: Platform.OS === 'web' })) return;
    if (route.params.id) {
      void openURL(getShareListingUrl(route.params.id));
    }
  }, [isSmallUI, isLoggedIn, isStarting, route.params.id]);

  const { listingDistanceQuery, listingQuery, userQuery, t } = listingHook;

  useEffect(() => {
    if (!isGuestWeb || !listingQuery.isError || !navigationRef.isReady()) return;
    navigationRef.navigate(
      'main',
      {
        screen: 'billboard',
        params: { screen: 'all-listings', params: { activeTab: ListingType.offer, type: ListingType.offer } },
      },
      { pop: true },
    );
  }, [isGuestWeb, listingQuery.isError]);

  const getListingTrackingContext = useEffectEvent(() => ({
    listing: listingQuery.data,
    listingId: route.params.id,
  }));

  useEffect(() => {
    const { listing, listingId } = getListingTrackingContext();
    if (listing) {
      if (listing.categoryPrimary.id) {
        track(Event.ListingOpened, {
          listing_id: listingId,
          listing_type: listing.type,
          listing_category: listing.categoryPrimary.id,
          listing_subtype: listing.subtype,
          listing_service_category: listing.serviceCategory?.id,
          is_quick_offer: isQuickOffer(listing),
        });
      }
    }

    return () => {
      if (listing?.categoryPrimary.id) {
        track(Event.ListingClosed, {
          listing_id: listingId,
          listing_type: listing.type,
          listing_category: listing.categoryPrimary.id,
          listing_subtype: listing.subtype,
          listing_service_category: listing.serviceCategory?.id,
          is_quick_offer: isQuickOffer(listing),
        });
      }
    };
  }, []);

  const screenTitle = !listingQuery.data
    ? ''
    : t(`title.${listingQuery.data.type}${isDonationListing(listingQuery.data) ? 'Donation' : ''}`);

  const handleCommentInputFocus = (input: TextInput | null) => {
    setIsCommentInputFocused(true);

    const inputHandle = input ? findNodeHandle(input) : null;

    if (!inputHandle || !pageRef.current || !('scrollToFocusedInput' in pageRef.current)) {
      return;
    }

    requestAnimationFrame(() => {
      if (pageRef.current && 'scrollToFocusedInput' in pageRef.current) {
        pageRef.current.scrollToFocusedInput(inputHandle, COMMENT_INPUT_EXTRA_SCROLL_HEIGHT);
      }
    });
  };

  const handleCommentInputBlur = () => {
    setIsCommentInputFocused(false);
  };

  // Always call unconditionally to preserve React hook order across renders
  useNavigationOptions(
    navigation,
    listingQuery.data
      ? {
          headerShown: false,
        }
      : isInModal
        ? {
            headerShown: true,
            ...(navigation.getState().index === 0 ? { headerBackVisible: false } : {}),
            headerTitle: () => (
              <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
                {screenTitle}
              </Text>
            ),
          }
        : { headerShown: isLoggedIn },
  );

  const setPageTitle = usePageTitle();
  useEffect(
    () => listingQuery.data && setPageTitle(`${screenTitle} - {{name}}`, { name: listingQuery.data.item.name }),
    [listingQuery.data, screenTitle, setPageTitle],
  );

  useRegisterLoading(listingQuery.isPending, 'OneListingScreen');
  useRegisterLoading(isLoggedIn && listingDistanceQuery.isPending, 'OneListingScreen');
  useRegisterLoading(isLoggedIn && userQuery.isPending, 'OneListingScreen');

  if (
    (listingQuery.data?.status === 'deleted' || listingQuery.data?.status === 'archived') &&
    listingQuery.data.userId !== userQuery.data?.id // Allow users to see their own deleted/archived listings, if they appear in a list somewhere
  ) {
    return <UnavailableListingNotice t={t} />;
  }

  if (!listingQuery.data) {
    return <OneListingSkeleton />;
  }

  return (
    <LoadedOneListingScreen
      defaultPaddingStyle={defaultPaddingStyle}
      fromNewScreen={route.params.fromNewScreen}
      handleCommentInputBlur={handleCommentInputBlur}
      handleCommentInputFocus={handleCommentInputFocus}
      isCommentInputFocused={isCommentInputFocused}
      isSmallUI={isSmallUI}
      isUnavailabilityBannerVisible={isUnavailabilityBannerVisible}
      listingHook={listingHook}
      pageRef={pageRef}
      scrollOffsetRef={scrollOffsetRef}
      scrollViewRef={scrollViewRef}
      setIsUnavailabilityBannerVisible={setIsUnavailabilityBannerVisible}
      setUnavailabilityBannerHeight={setUnavailabilityBannerHeight}
      t={t}
      unavailabilityBannerHeight={unavailabilityBannerHeight}
    />
  );
};

const OneListingScreenWithMetrics = withOptionalMetrics(
  'OneListingScreen',
  withErrorBoundary(OneListingScreenComponent),
);

export const OneListingScreen: NavigationScreen<'one-listing'> = (props) => <OneListingScreenWithMetrics {...props} />;

function isFromNewListingScreen(routeParams: ReturnType<typeof useListing>['routeParams']) {
  return 'fromNewScreen' in routeParams && Boolean(routeParams.fromNewScreen);
}

function useListingViewState({
  canUseSocialFeed,
  isAuthor,
  listingHook,
  numPosts,
  t,
}: {
  canUseSocialFeed: boolean;
  isAuthor: boolean;
  listingHook: ReturnType<typeof useListing>;
  numPosts: number;
  t: TFunction;
}) {
  const listing = listingHook.listingQuery.data!;
  const [assistantSheetVisible, setAssistantSheetVisible] = useState(false);
  const othersListingScrollY = useSharedValue(0);
  const handleOthersListingScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      othersListingScrollY.set(event.contentOffset.y);
    },
  });
  const activeTabContext = `${String(canUseSocialFeed)}:${listing.type}:${String(numPosts > 0)}`;
  const [activeTabState, setActiveTabState] = useState({ context: activeTabContext, value: 'info' });
  let currentActiveTabState = activeTabState;
  if (activeTabState.context !== activeTabContext) {
    currentActiveTabState = { context: activeTabContext, value: 'info' };
    setActiveTabState(currentActiveTabState);
  }
  const activeTab = currentActiveTabState.value;
  const setActiveTab = (value: string) => setActiveTabState({ context: activeTabContext, value });

  const tabs = [
    { label: t('tabs.info', { ns: 'one-listing' }), value: 'info' },
    ...(canUseSocialFeed
      ? [{ label: t('tabs.stories', { ns: 'one-listing', count: numPosts }), value: 'stories' }]
      : []),
  ];

  useEffect(() => {
    othersListingScrollY.set(0);
  }, [activeTab, othersListingScrollY]);

  const showConciergerie = isFeatureEnabled(FeatureFlag.conciergerie, listingHook.userQuery.data?.featureFlags);
  const [assistantSheet2Dismissed, setAssistantSheet2Dismissed] = useState(false);

  const conciergerieContent =
    isAuthor && listing.type === ListingType.request && showConciergerie ? (
      <View style={{ marginHorizontal: theme.spacing['spacing.general.x3'] }}>
        <ConciergerieSection
          key={String(listing.conciergerieDisabledByUser)}
          listing={listing}
          onUpdate={(data, options) => listingHook.updateListing.mutate(data, options)}
          onPressMoreDetails={() => setAssistantSheetVisible(true)}
          onPressViewResponses={() => navigateToAllChannels(listingHook.navigation, { listingId: listing.id })}
        />
      </View>
    ) : null;

  const assistantSheets =
    isAuthor && showConciergerie ? (
      <>
        <AssistantBottomSheet visible={assistantSheetVisible} onClose={() => setAssistantSheetVisible(false)} />
        <AssistantBottomSheet2
          visible={isFromNewListingScreen(listingHook.routeParams) && !assistantSheet2Dismissed}
          onClose={() => setAssistantSheet2Dismissed(true)}
        />
      </>
    ) : null;

  return {
    activeTab,
    assistantSheets,
    conciergerieContent,
    handleOthersListingScroll,
    othersListingScrollY,
    setActiveTab,
    tabs,
  };
}

function VisitorListingActionAccessory({
  defaultPaddingStyle,
  listingHook,
  scrollOffsetRef,
  scrollViewRef,
  variant,
}: {
  defaultPaddingStyle: ReturnType<typeof usePagePadding>;
  listingHook: ReturnType<typeof useListing>;
  scrollOffsetRef: React.RefObject<number>;
  scrollViewRef: React.RefObject<ScrollView | null>;
  variant: {
    audience: 'author' | 'visitor';
    commentInput: 'focused' | 'idle';
    layout: 'small' | 'large';
    participants: 'capped' | 'open';
  };
}) {
  const isAuthor = variant.audience === 'author';
  const hasMaxParticipants = variant.participants === 'capped';
  const { height: windowHeight } = useWindowDimensions();
  const { viewRef: contactMemberButtonRef } = useTutorialRef(
    !isAuthor && !hasMaxParticipants ? TutorialRef.ContactMemberButton : undefined,
  );
  const { startTutorial, hasCompletedTutorial, completeTutorial } = useTutorials();
  const { hideTutorialOverlay } = useTutorialOverlay();
  const contactTutorialStarted = useRef(false);
  const visibilityTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current);
    },
    [],
  );

  const onContactButtonLayout = () => {
    if (contactTutorialStarted.current || hasCompletedTutorial(Tutorial.ContactMember)) return;

    const ref = contactMemberButtonRef?.current;
    if (!ref) return;

    contactTutorialStarted.current = true;

    if (Platform.OS === 'web') {
      (ref as unknown as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      scheduleContactTutorial(visibilityTimerRef, mountedRef, startTutorial, 800);
      return;
    }

    const scrollView = scrollViewRef.current;
    const settleDelay = Platform.OS === 'android' ? 900 : 600;
    ref.measureInWindow((_x, windowY, _w, height) => {
      if (!mountedRef.current) return;
      const delta = windowY - (windowHeight / 2 - height / 2);
      const targetY = Math.max(0, scrollOffsetRef.current + delta);
      scrollView?.scrollTo({ y: targetY, animated: true });
      scheduleContactTutorial(visibilityTimerRef, mountedRef, startTutorial, settleDelay);
    });
  };

  const onContactPress = () => {
    if (getCurrentTutorial() === Tutorial.ContactMember) {
      completeTutorial(true, Tutorial.ContactMember);
      void hideTutorialOverlay();
    }
    listingHook.contactButtonHandler();
  };

  if (
    isAuthor ||
    (Boolean(listingHook.listingQuery.data?.guardianId) &&
      listingHook.listingQuery.data?.guardianId === listingHook.userQuery.data?.id) ||
    variant.commentInput === 'focused' ||
    hasMaxParticipants ||
    listingHook.listingQuery.data?.user.profile.placeDetails?.allowInAppContact === false
  )
    return null;

  return (
    <ListingActionAccessory
      contactButtonRef={contactMemberButtonRef}
      contactError={listingHook.createChannel.error}
      contactPending={listingHook.createChannel.isPending}
      isSmallUI={variant.layout === 'small'}
      onContactButtonLayout={onContactButtonLayout}
      onContactPress={onContactPress}
      paddingBottom={defaultPaddingStyle.paddingBottom}
      paddingHorizontal={defaultPaddingStyle.paddingLeft}
    />
  );
}

function ListingEditAccessory({
  defaultPaddingStyle,
  listingHook,
  t,
}: {
  defaultPaddingStyle: ReturnType<typeof usePagePadding>;
  listingHook: ReturnType<typeof useListing>;
  t: TFunction;
}) {
  const listing = listingHook.listingQuery.data!;
  return (
    <Accessory
      style={{
        paddingHorizontal: defaultPaddingStyle.paddingLeft,
        marginTop: theme.spacing['spacing.general.x2'],
      }}
    >
      <Button
        authorize
        flex={1}
        height={48}
        justifyContent="center"
        minHeight={48}
        onPress={() => listingHook.navigation.navigate('edit-listing', { id: listing.id }, { pop: true })}
        style={{
          backgroundColor: theme.colors['surface.inverse.default'],
          borderRadius: theme.borderRadii['radius.xl'],
        }}
        variant="primary"
      >
        <IconEdit color="white" size={20} style={{ marginRight: theme.spacing['spacing.general.x2'] }} />
        <Text color="text.inverse.primary" fontWeight="700" textAlign="center" variant="bodyBase">
          {t('headerSection.editButtonText')}
        </Text>
      </Button>
    </Accessory>
  );
}

function ListingActiveTab({
  activeTab,
  bottomAccessory,
  conciergerieContent,
  handleCommentInputBlur,
  handleCommentInputFocus,
  hasUnavailabilityBanner,
  listingHook,
  listingTabs,
  onScroll,
  pageRef,
  postsQuery,
  scrollOffsetRef,
  scrollViewRef,
  scrollY,
  unavailabilityBannerHeight,
}: {
  activeTab: string;
  bottomAccessory: React.JSX.Element | null;
  conciergerieContent: React.JSX.Element | null;
  handleCommentInputBlur: () => void;
  handleCommentInputFocus: (input: TextInput | null) => void;
  hasUnavailabilityBanner: boolean;
  listingHook: ReturnType<typeof useListing>;
  listingTabs: React.JSX.Element | null;
  onScroll: React.ComponentProps<typeof OthersListing>['onScroll'];
  pageRef: React.RefObject<OneListingPageRef | null>;
  postsQuery: ReturnType<typeof usePostsByListing>;
  scrollOffsetRef: React.RefObject<number>;
  scrollViewRef: React.RefObject<ScrollView | null>;
  scrollY: React.ComponentProps<typeof OthersListing>['scrollY'];
  unavailabilityBannerHeight: number;
}) {
  const { listingDistanceQuery, listingQuery } = listingHook;
  const listing = listingQuery.data!;
  const refreshControl = (
    <RefreshControl
      onRefresh={() =>
        void Promise.all([listingQuery.refetch(), ...(listingHook.isAuthor ? [] : [listingDistanceQuery.refetch()])])
      }
      refreshing={listingQuery.isRefetching || (!listingHook.isAuthor && listingDistanceQuery.isRefetching)}
    />
  );

  if (activeTab !== 'info') {
    return (
      <PostsList
        ListHeaderComponent={
          <OthersListingHeader
            listing={{
              ...listing,
              hasUnavailabilityBanner,
              unavailabilityBannerHeight,
              onScroll,
              scrollY,
              tabs: listingTabs,
            }}
          />
        }
        isLoading={postsQuery.isPending}
        onScroll={onScroll}
        posts={postsQuery.data ?? []}
      />
    );
  }

  return (
    <OthersListing
      {...listing}
      {...listingDistanceQuery.data}
      bottomAccessory={bottomAccessory}
      hasUnavailabilityBanner={hasUnavailabilityBanner}
      unavailabilityBannerHeight={unavailabilityBannerHeight}
      onCommentInputBlur={handleCommentInputBlur}
      onCommentInputFocus={handleCommentInputFocus}
      refreshControl={refreshControl}
      scrollViewRef={scrollViewRef}
      scrollOffsetRef={scrollOffsetRef}
      setPageRef={(ref) => {
        pageRef.current = ref;
      }}
      onScroll={onScroll}
      scrollY={scrollY}
      tabs={listingTabs}
      topContent={conciergerieContent}
    />
  );
}

function ListingAvailabilityBoundary({
  activeTab,
  children,
  isAuthor,
  listing,
}: {
  activeTab: string;
  children: React.JSX.Element;
  isAuthor: boolean;
  listing: NonNullable<ReturnType<typeof useListing>['listingQuery']['data']>;
}) {
  const hasAvailabilityCalendar =
    activeTab === 'info' &&
    shouldShowListingAvailabilityCalendar({
      listing,
      isAuthor,
      isHauteGaspesie: isWhiteLabel(WhiteLabel.HauteGaspesie),
    });
  if (!hasAvailabilityCalendar) return children;
  return <ListingAvailabilityCalendarProvider listing={listing}>{children}</ListingAvailabilityCalendarProvider>;
}

function ListingWithTabs({
  defaultPaddingStyle,
  handleCommentInputBlur,
  handleCommentInputFocus,
  listingHook,
  pageRef,
  scrollViewRef,
  scrollOffsetRef,
  t,
  variant,
}: {
  defaultPaddingStyle: ReturnType<typeof usePagePadding>;
  handleCommentInputBlur: () => void;
  handleCommentInputFocus: (input: TextInput | null) => void;
  listingHook: ReturnType<typeof useListing>;
  pageRef: React.RefObject<OneListingPageRef | null>;
  t: TFunction;
  scrollViewRef: React.RefObject<ScrollView | null>;
  scrollOffsetRef: React.RefObject<number>;
  variant: {
    origin: 'new-listing' | 'standard';
    viewer: 'author' | 'visitor';
    commentInput: 'focused' | 'blurred';
    viewport: 'small' | 'large';
    unavailabilityBanner: { state: 'hidden' } | { state: 'visible'; height: number };
  };
}) {
  const isAuthor = variant.viewer === 'author';
  const isCommentInputFocused = variant.commentInput === 'focused';
  const isSmallUI = variant.viewport === 'small';
  const hasUnavailabilityBanner = variant.unavailabilityBanner.state === 'visible';
  const unavailabilityBannerHeight =
    variant.unavailabilityBanner.state === 'visible' ? variant.unavailabilityBanner.height : 0;
  const { listingQuery } = listingHook;
  const listing = listingQuery.data!;
  const { canUseSocialFeed } = useSocialFeedFeature();
  const postsQuery = usePostsByListing(listing.id, { enabled: canUseSocialFeed });
  const numPosts = postsQuery.data?.length ?? 0;
  const {
    activeTab,
    assistantSheets,
    conciergerieContent,
    handleOthersListingScroll,
    othersListingScrollY,
    setActiveTab,
    tabs,
  } = useListingViewState({ canUseSocialFeed, isAuthor, listingHook, numPosts, t });

  const hasMaxParticipants = listing.maxParticipants !== undefined && listing.maxParticipants !== null;
  const listingTabs =
    listing.type === ListingType.offer && numPosts > 0 ? (
      <Tabs value={activeTab} onPress={setActiveTab} tabs={tabs} />
    ) : null;
  const visitorAccessory = (
    <VisitorListingActionAccessory
      defaultPaddingStyle={defaultPaddingStyle}
      listingHook={listingHook}
      scrollOffsetRef={scrollOffsetRef}
      scrollViewRef={scrollViewRef}
      variant={{
        audience: isAuthor ? 'author' : 'visitor',
        commentInput: isCommentInputFocused ? 'focused' : 'idle',
        layout: isSmallUI ? 'small' : 'large',
        participants: hasMaxParticipants ? 'capped' : 'open',
      }}
    />
  );
  const editAccessory = (
    <ListingEditAccessory defaultPaddingStyle={defaultPaddingStyle} listingHook={listingHook} t={t} />
  );
  const bottomAccessory = isAuthor ? editAccessory : visitorAccessory;

  const content = (
    <View collapsable={false} style={{ flex: 1, minHeight: 0 }}>
      <ListingActiveTab
        activeTab={activeTab}
        bottomAccessory={bottomAccessory}
        conciergerieContent={conciergerieContent}
        handleCommentInputBlur={handleCommentInputBlur}
        handleCommentInputFocus={handleCommentInputFocus}
        hasUnavailabilityBanner={hasUnavailabilityBanner}
        listingHook={listingHook}
        listingTabs={listingTabs}
        onScroll={handleOthersListingScroll}
        pageRef={pageRef}
        postsQuery={postsQuery}
        scrollOffsetRef={scrollOffsetRef}
        scrollViewRef={scrollViewRef}
        scrollY={othersListingScrollY}
        unavailabilityBannerHeight={unavailabilityBannerHeight}
      />

      <OthersListingAnimatedChrome
        bottomAccessory={bottomAccessory}
        listing={{
          ...listing,
          hasUnavailabilityBanner,
          unavailabilityBannerHeight,
          onScroll: handleOthersListingScroll,
          scrollY: othersListingScrollY,
        }}
      />
      {assistantSheets}
    </View>
  );

  return (
    <ListingAvailabilityBoundary activeTab={activeTab} isAuthor={isAuthor} listing={listing}>
      {content}
    </ListingAvailabilityBoundary>
  );
}

export const OneListingSkeleton = function OneListingScreenSkeleton() {
  const leftElement = (props: ViewProps) => (
    <PlaceholderMedia
      style={[
        {
          height: 174,
          width: '100%',
          borderRadius: theme.borderRadii['radius.lg'],
          marginEnd: theme.spacing['spacing.general.x4'],
        },
        props.style,
      ]}
    />
  );

  return (
    <Page>
      <Column>
        <Placeholder Animation={ShineOverlay} Left={leftElement} />
        <Row alignItems="center" gap="spacing.general.x4" justifyContent="space-between" marginTop="spacing.general.x8">
          <PlaceholderLine
            style={{
              height: 20,
              width: 200,
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          />

          <PlaceholderLine
            style={{
              height: 20,
              width: 20,
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          />
        </Row>
        <Column gap="spacing.general.x4" marginTop="spacing.general.x5">
          <PlaceholderMedia
            style={{
              height: 48,
              width: '100%',
              borderRadius: theme.borderRadii['radius.lg'],
              marginEnd: theme.spacing['spacing.general.x4'],
            }}
          />
        </Column>
        <Column marginTop="spacing.general.x4">
          <PlaceholderLine
            style={{
              height: 20,
              width: 192,
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          />

          <PlaceholderLine
            style={{
              height: 20,
              width: 192,
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          />

          <PlaceholderLine
            style={{
              height: 40,
              width: 100,
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          />
        </Column>
        <Column marginTop="spacing.general.x5">
          <PlaceholderLine
            style={{
              height: 2,
              width: '100%',
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          />

          <PosterSection.Skeleton />
          <PlaceholderLine
            style={{
              height: 2,
              width: '100%',
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          />
        </Column>
      </Column>
    </Page>
  );
};
